/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  Coverage,
  CoverageCollection,
  EDRInstance,
  PointDomain,
  PointSeriesDomain,
} from '@opengeoweb/shared';

// This function is a copy of /libs/api/src/utils.ts which cannot be used here due to circular dependency
export const handleResponse = async (response: Response): Promise<unknown> => {
  if (response.ok) {
    return response
      .json()
      .then(async (json) => {
        return (json || response.headers) as Promise<unknown>;
      })
      .catch(() => {
        // No JSON response
      });
  }
  return response
    .json()
    .catch(() => {
      // Couldn't parse the JSON
      throw new Error(`${response.status}`);
    })
    .then((json) => {
      // Got valid JSON with error response, use it
      if (json) {
        const { message } = json;
        if (message) {
          throw new Error(message, { cause: response.status });
        }
        const result = JSON.stringify(json);
        throw new Error(result);
      }
      throw new Error(`${response.status}`);
    });
};

export const joinUrlParams = (params: string[]): string => {
  return params.filter((x) => x !== undefined && x.length > 0).join('/');
};

// This is a copy from libs/api/src/utils.ts due to circular dependency
const withJsonFormat = (url: string): string => {
  const urlObj = new URL(url);
  urlObj.searchParams.set('f', 'json');
  return urlObj.toString();
};

export const fetchEDRCollectionDetails = async (
  edrBaseUrlWithCollection: string,
  instanceId = '',
): Promise<EDRInstance> => {
  return fetch(
    withJsonFormat(joinUrlParams([edrBaseUrlWithCollection, instanceId])),
  ).then(handleResponse) as Promise<EDRInstance>;
};

export const fetchEDRCube = async (
  edrBaseUrlWithCollection: string,
  parameterName: string,
  datetime = '',
  instanceId = '',
  bbox = '-180,-90,180,90',
): Promise<
  | CoverageCollection<PointDomain | PointSeriesDomain>
  | Coverage<PointDomain | PointSeriesDomain>
> => {
  const queryParams = new URLSearchParams();
  queryParams.append('bbox', bbox);
  queryParams.append('parameter-name', parameterName);
  datetime && queryParams.append('datetime', datetime);
  const url = `${joinUrlParams([edrBaseUrlWithCollection, instanceId, 'cube'])}?${queryParams.toString()}`;
  return fetch(url).then(handleResponse) as Promise<
    | CoverageCollection<PointDomain | PointSeriesDomain>
    | Coverage<PointDomain | PointSeriesDomain>
  >;
};

export const fetchEDRArea = async (
  edrBaseUrlWithCollection: string,
  parameterName: string,
  datetime = '',
  instanceId = '',
  coords = 'POLYGON((-180 -90, -180 90, 180 90, 180 -90, -180 -90))',
): Promise<
  | CoverageCollection<PointDomain | PointSeriesDomain>
  | Coverage<PointDomain | PointSeriesDomain>
> => {
  const queryParams = new URLSearchParams();
  queryParams.append('coords', coords);
  queryParams.append('parameter-name', parameterName);
  datetime && queryParams.append('datetime', datetime);
  const url = `${joinUrlParams([edrBaseUrlWithCollection, instanceId, 'area'])}?${queryParams.toString()}`;
  return fetch(url).then(handleResponse) as Promise<
    | CoverageCollection<PointDomain | PointSeriesDomain>
    | Coverage<PointDomain | PointSeriesDomain>
  >;
};

export const fetchEDRParameter = async (
  collection: EDRInstance | undefined,
  edrBaseUrlWithCollection: string,
  parameterName: string,
  datetime = '',
  instanceId = '',
): Promise<
  | CoverageCollection<PointDomain | PointSeriesDomain>
  | Coverage<PointDomain | PointSeriesDomain>
  | undefined
> => {
  const bbox = collection?.extent?.spatial.bbox
    ? collection?.extent?.spatial.bbox[0]
    : undefined;
  if (collection?.data_queries?.cube) {
    const bboxString = bbox ? bbox.join(',') : undefined;

    return fetchEDRCube(
      edrBaseUrlWithCollection,
      parameterName,
      datetime,
      instanceId,
      bboxString,
    );
  }
  if (collection?.data_queries?.area) {
    const bboxString = bbox
      ? `POLYGON((${bbox[0]} ${bbox[1]}, ${bbox[2]} ${bbox[1]}, ${bbox[2]} ${bbox[3]}, ${bbox[0]} ${bbox[3]} , ${bbox[0]} ${bbox[1]}))`
      : undefined;

    return fetchEDRArea(
      edrBaseUrlWithCollection,
      parameterName,
      datetime,
      instanceId,
      bboxString,
    );
  }
  return undefined;
};
